// src/contexts/PartnerAuthContext.js
// Authentication context for contract company partner portal

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const PartnerAuthContext = createContext();

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext);
  if (!context) {
    throw new Error('usePartnerAuth must be used within a PartnerAuthProvider');
  }
  return context;
};

export const PartnerAuthProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('partnerToken');
    if (token) {
      checkAuth(token);
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async (token) => {
    try {
      const response = await axios.get('/api/contract-companies/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(response.data.company);
    } catch (err) {
      setCompany(null);
      localStorage.removeItem('partnerToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post('/api/contract-companies/login', { email, password });
      const { company, token } = response.data;
      localStorage.setItem('partnerToken', token);
      setCompany(company);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    setCompany(null);
    localStorage.removeItem('partnerToken');
  };

  const getToken = () => localStorage.getItem('partnerToken');

  const value = {
    company,
    loading,
    error,
    login,
    logout,
    getToken,
    isAuthenticated: !!company
  };

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export default PartnerAuthContext;
